import { Tooltip } from '@chakra-ui/react';
import classNames from 'classnames';
import { extent, scaleBand, scaleLinear } from 'd3';
import type { ReactNode } from 'react';
import { useEffect } from 'react';

import { ReplotChartNames } from '../../constants';
import { type CommonPlotProps, FormatType } from '../../types';
import {
  getFormatter,
  getMaxTextWidth,
  plotColors,
  useResizeObserver,
} from '../../utilities';
import { plotTestId } from '../../utilities/plotTestId';
import { AxisLabel } from '../axis/axis-label';
import axisLabelStyles from '../axis/axis-label.module.css';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import styles from './diverging-bar-chart.module.css';

export type DivergingBarLayout = { centerX: number };

const Dimensions = {
  BarLeftPadding: 4,
  paddingInner: 0.4,
  paddingOuter: 0.4,
};

export type DivergingBarData = {
  label: string;
  leftValue: number | null;
  rightValue: number | null;
  leftLabel: string;
  rightLabel: string;
  leftId?: string | null;
  rightId?: string | null;
};

export type DivergingBarChartProps = {
  data: DivergingBarData[];
  leftColor?: string;
  rightColor?: string;
  showBarLabels?: boolean;
  tooltipContent?: (
    data: DivergingBarData,
    side: 'left' | 'right'
  ) => JSX.Element;
  valueLabel?: (data: DivergingBarData, side: 'left' | 'right') => string;
  onLayout?: (layout: DivergingBarLayout) => void;
  footer?: (layout: DivergingBarLayout) => ReactNode;
  footerHeight?: number;
  /** When true (default), data is sorted by net difference (rightValue + leftValue) descending. Set to false when parent provides pre-sorted data. */
  sortData?: boolean;
  /** Optional labels shown above the left and right bar areas (e.g. entity names in dual-view comparison). */
  leftHeaderLabel?: string;
  rightHeaderLabel?: string;
  /** Reserve space at top so bar area aligns with another chart that has a header (e.g. dual-view). */
  topOffset?: number;
  /** When false, y-axis row labels are not rendered (e.g. second chart in dual-view). Default true. */
  showYAxisLabels?: boolean;
} & CommonPlotProps;

const DEFAULT_FOOTER_HEIGHT = 25;
/** Height of the optional header row (entity labels). Export for dual-view alignment. */
export const DIVERGING_BAR_HEADER_LABEL_HEIGHT = 20;

export const DivergingBarChart = ({
  className,
  data,
  loading = false,
  format = FormatType.PERCENTAGE,
  leftColor = plotColors?.[0] ?? '#5CA9E9',
  rightColor = plotColors?.[1] ?? '#70CA97',
  showBarLabels = false,
  tooltipContent,
  valueLabel,
  onLayout,
  footer,
  footerHeight = DEFAULT_FOOTER_HEIGHT,
  sortData = true,
  leftHeaderLabel,
  rightHeaderLabel,
  topOffset = 0,
  showYAxisLabels = true,
}: DivergingBarChartProps) => {
  const { containerRef, width, height } = useResizeObserver();

  const hasHeaderLabels =
    (leftHeaderLabel !== null &&
      leftHeaderLabel !== undefined &&
      leftHeaderLabel !== '') ||
    (rightHeaderLabel !== null &&
      rightHeaderLabel !== undefined &&
      rightHeaderLabel !== '');
  const headerHeight = hasHeaderLabels ? DIVERGING_BAR_HEADER_LABEL_HEIGHT : 0;
  const contentHeight = footer ? Math.max(0, height - footerHeight) : height;
  const plotHeight = Math.max(0, contentHeight - headerHeight - topOffset);
  const labelWidth =
    !showYAxisLabels || data.length === 0
      ? 0
      : Math.min(
          getMaxTextWidth({ texts: data.map((d) => d.label) }),
          width * 0.35
        );
  const margin = labelWidth + Dimensions.BarLeftPadding;
  const plotWidth = Math.max(0, width - labelWidth - Dimensions.BarLeftPadding);
  const isPlotSizeValid = plotWidth > 0 && plotHeight > 0;

  const centerX = margin + plotWidth / 2;
  useEffect(() => {
    onLayout?.({ centerX });
  }, [onLayout, centerX]);

  const sortedData = sortData
    ? [...data].sort((a, b) => {
        const aDiff = (a.rightValue ?? 0) + (a.leftValue ?? 0);
        const bDiff = (b.rightValue ?? 0) + (b.leftValue ?? 0);
        return bDiff - aDiff;
      })
    : [...data];

  const yScale = scaleBand()
    .domain(sortedData.map((d) => d.label))
    .range([0, plotHeight])
    .paddingInner(Dimensions.paddingInner)
    .paddingOuter(Dimensions.paddingOuter);

  const allValues = sortedData
    .flatMap((d) => [d.leftValue, d.rightValue])
    .filter((v): v is number => v !== null);
  const [minValue, maxValue] = extent(allValues);

  const valueScale = (() => {
    if (minValue === undefined || maxValue === undefined) return null;
    const absMax = Math.max(Math.abs(minValue), Math.abs(maxValue));
    return scaleLinear([-absMax, absMax], [0, plotWidth]);
  })();

  const formatValue = getFormatter(format);

  const renderBar = (item: DivergingBarData) => {
    const y = yScale(item.label);
    if (y === undefined || !valueScale) return null;

    const centerX = valueScale(0);
    const leftValue = item.leftValue;
    const rightValue = item.rightValue;
    const hasLeft = typeof leftValue === 'number';
    const hasRight = typeof rightValue === 'number';

    const leftX = hasLeft ? valueScale(leftValue) : centerX;
    const rightX = hasRight ? valueScale(rightValue) : centerX;

    const leftBarWidth = hasLeft ? Math.abs(centerX - leftX) : 0;
    const rightBarWidth = hasRight ? Math.abs(rightX - centerX) : 0;

    const leftBarWidthForRect =
      hasLeft && (leftValue === 0 || leftBarWidth === 0)
        ? Math.max(1, leftBarWidth)
        : leftBarWidth;
    const rightBarWidthForRect =
      hasRight && (rightValue === 0 || rightBarWidth === 0)
        ? Math.max(1, rightBarWidth)
        : rightBarWidth;

    const leftBarRectX = (() => {
      if (!hasLeft) return centerX;
      if (leftValue === 0 || leftBarWidth === 0) {
        return centerX - leftBarWidthForRect;
      }
      return Math.min(leftX, centerX);
    })();

    const leftBarLabel = (() => {
      if (!hasLeft) return '';
      if (valueLabel) return valueLabel(item, 'left');
      return formatValue(leftValue);
    })();

    const rightBarLabel = (() => {
      if (!hasRight) return '';
      if (valueLabel) return valueLabel(item, 'right');
      return formatValue(rightValue);
    })();

    const labelPadding = 6;

    return (
      <g key={item.label}>
        {hasLeft && (
          <Tooltip
            hasArrow
            label={
              tooltipContent ? (
                tooltipContent(item, 'left')
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    {item.label}
                  </div>
                  <div>
                    {item.leftLabel}: {formatValue(leftValue ?? 0)}
                  </div>
                </div>
              )
            }
            padding="8px 12px"
            placement="top"
            bg="var(--ui-text-normal, #2d426a)"
            borderRadius="4px"
          >
            <rect
              x={leftBarRectX}
              y={y}
              width={leftBarWidthForRect}
              height={yScale.bandwidth()}
              rx={2}
              fill={leftColor}
              className={styles.bar}
              data-testid="diverging-bar-chart-bar-left"
            />
          </Tooltip>
        )}
        {showBarLabels &&
          hasLeft &&
          (() => {
            const labelWidthPx = leftBarLabel.length * 6;
            const fitsInside = leftBarWidthForRect > labelWidthPx + 8;

            // biome-ignore lint/suspicious/noImplicitAnyLet: migrated from develop
            let x, textAnchor, fill;

            if (fitsInside) {
              // Inside left bar at extreme left end, align left
              x = Math.min(leftX, centerX) + labelPadding;
              textAnchor = 'start';
              fill = 'white';
            } else {
              // Outside left bar: use bar color
              x = Math.min(leftX, centerX) - labelPadding;
              textAnchor = 'end';
              fill = leftColor;
            }

            return (
              <text
                x={x}
                y={y + yScale.bandwidth() / 2}
                textAnchor={textAnchor}
                fontSize={11}
                fill={fill}
                dominantBaseline="middle"
                pointerEvents="none"
              >
                {leftBarLabel}
              </text>
            );
          })()}
        {hasRight && (
          <Tooltip
            hasArrow
            label={
              tooltipContent ? (
                tooltipContent(item, 'right')
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    {item.label}
                  </div>
                  <div>
                    {item.rightLabel}: {formatValue(rightValue ?? 0)}
                  </div>
                </div>
              )
            }
            padding="8px 12px"
            placement="top"
            bg="var(--ui-text-normal, #2d426a)"
            borderRadius="4px"
          >
            <rect
              x={centerX}
              y={y}
              width={rightBarWidthForRect}
              height={yScale.bandwidth()}
              rx={2}
              fill={rightColor}
              className={styles.bar}
              data-testid="diverging-bar-chart-bar-right"
            />
          </Tooltip>
        )}
        {showBarLabels &&
          hasRight &&
          (() => {
            const labelWidthPx = rightBarLabel.length * 6;
            const fitsInside = rightBarWidthForRect > labelWidthPx + 8;

            // biome-ignore lint/suspicious/noImplicitAnyLet: migrated from develop
            let x, textAnchor, fill;

            if (fitsInside) {
              // Inside right bar at extreme right end, align right
              x = Math.max(centerX, rightX) - labelPadding;
              textAnchor = 'end';
              fill = 'white';
            } else {
              // Outside right bar: use bar color
              x = Math.max(centerX, rightX) + labelPadding;
              textAnchor = 'start';
              fill = rightColor;
            }

            return (
              <text
                x={x}
                y={y + yScale.bandwidth() / 2}
                textAnchor={textAnchor}
                fontSize={11}
                fill={fill}
                dominantBaseline="middle"
                pointerEvents="none"
              >
                {rightBarLabel}
              </text>
            );
          })()}
      </g>
    );
  };

  const renderAxisLabel = (label: string) => {
    if (!showYAxisLabels) return null;
    const y = yScale(label);
    if (y === undefined) return null;

    return (
      <AxisLabel
        key={label}
        y={y + yScale.bandwidth() / 2}
        width={labelWidth}
        fontSize={11}
        label={label}
        availableHeight={yScale.bandwidth()}
        testId="diverging-bar-chart-axis-label"
      />
    );
  };

  const layout: DivergingBarLayout = { centerX };

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, className)}
      style={
        footer || hasHeaderLabels || topOffset > 0
          ? {
              display: 'flex',
              flexDirection: 'column',
            }
          : undefined
      }
      data-testid={plotTestId(ReplotChartNames.DivergingBarChart)}
    >
      {topOffset > 0 && (
        <div
          style={{
            flexShrink: 0,
            height: topOffset,
            width: '100%',
          }}
        />
      )}
      {hasHeaderLabels && (
        <div
          className={styles.headerRow}
          style={{
            height: headerHeight,
            paddingLeft: margin,
          }}
        >
          <div className={styles.headerGrid}>
            <span
              className={classNames(
                axisLabelStyles.yAxisLabel,
                styles.headerLabel,
                styles.headerLabelLeft
              )}
              title={leftHeaderLabel ?? ''}
            >
              {leftHeaderLabel ?? ''}
            </span>
            <span
              className={classNames(
                axisLabelStyles.yAxisLabel,
                styles.headerLabel,
                styles.headerLabelRight
              )}
              title={rightHeaderLabel ?? ''}
            >
              {rightHeaderLabel ?? ''}
            </span>
          </div>
        </div>
      )}
      <PlotLoadWrapper loading={loading} noData={sortedData.length === 0}>
        <div
          style={{
            width: '100%',
            flex:
              footer || hasHeaderLabels || topOffset > 0
                ? '1 1 auto'
                : undefined,
            minHeight:
              footer || hasHeaderLabels || topOffset > 0 ? 0 : undefined,
          }}
        >
          <svg width="100%" height={plotHeight}>
            <g id="y-axis">
              {isPlotSizeValid && yScale.domain().map(renderAxisLabel)}
            </g>
            <g id="chart" transform={`translate(${margin}, 0)`}>
              {isPlotSizeValid && valueScale && sortedData.map(renderBar)}
            </g>
          </svg>
        </div>
      </PlotLoadWrapper>
      {footer && (
        <div
          style={{
            flexShrink: 0,
            height: footerHeight,
            width: '100%',
          }}
        >
          {footer(layout)}
        </div>
      )}
    </div>
  );
};
